<template>
  <main class="h-full">
    <section class="bg-h-primary-50 border py-2">
      <div class="container mx-auto">
        <BreadcrumbComponent :home="home" :items="items" />
      </div>
    </section>
    <section class="container mx-auto px-5 md:px-3 mt-10">
      <div class="grid grid-cols-12 items-start gap-3">
        <div class="col-span-12 md:col-span-3 flex flex-col gap-5 mt-6">
          <h4>
            {{ $t("labels.add", { pt: "Capítulo", en: "Chapter" }) }}
          </h4>
          <div
            class="rounded-lg border border-gray-200 overflow-hidden mt-[8px]"
          >
            <div class="flex flex-col w-100">
              <Button
                v-for="(item, i) in buttons"
                :key="i"
                :class="`${
                  i == active
                    ? 'btn-tab-active'
                    : item.done || !item.off
                    ? 'btn-tab-done'
                    : 'btn-tab-gray'
                }`"
                :disabled="item.off"
                @click="changeTab(item.active)"
              >
                <div class="flex justify-between items-center w-full">
                  <span class="hover:scale-95 transition-all">
                    {{ item.title }}
                  </span>
                  <v-icon
                    v-if="item.done"
                    name="hi-solid-check-circle"
                    class="text-h-success-400"
                  />
                  <v-icon
                    v-if="!isEditPage && !item.done"
                    name="hi-solid-x-circle"
                    class="text-h-error-400"
                    :title="item.info"
                  />
                </div>
              </Button>
            </div>
          </div>
        </div>
        <div class="col-span-12 md:col-span-9">
          <TabView class="custom-tabs-objective" :activeIndex="active" lazy>
            <TabPanel>
              <FormInfo
                v-if="course.id"
                @onNext="changeTab(1)"
                @onAllow="allowSteps"
                @onGetCourseById="getCourseById"
                :role="user.role"
                :chapter="chapter"
                :predecessorId="predecessorId"
              />
            </TabPanel>
            <TabPanel>
              <ObjectiveForm
                v-if="course.id"
                :objectives="objectives"
                :chapter="chapter"
                @onNext="changeTab(2)"
                @onAllow="allowSteps"
                @on-update="getCourseById"
              />
            </TabPanel>
            <TabPanel>
              <Quiz
                v-if="showQuiz"
                @onAllow="allowSteps"
                :quizId="quizId"
                :data="quiz"
                :role="user.role"
                :objective="chapter"
                :course="course"
                :questions="questions"
              />
            </TabPanel>
          </TabView>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { Chapter } from "@/models/chapter.model";
import { Course } from "@/models/courses.model";
import { LearningObjective } from "@/models/learning-objective.model";
import { LessonPlan } from "@/models/lessonPlan.model";
import { QueryParams } from "@/models/query-params.model";
import {
  Question,
  QuestionDragDrop,
  QuestionTrueFalse,
  QuizTO,
} from "@/models/quiz.model";
import { useQuizQuestionsDragNDropStore } from "@/store/quiz-question-drag";
import { useQuizQuestionsStore } from "@/store/quiz-questions";
import { GoToRoute } from "@/utils/go-to.util";
import {
  removeItemLocalStorage,
  setItemLocalStorage,
} from "@/utils/save-storage.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { last, take } from "rxjs";
import { computed, defineAsyncComponent, defineComponent } from "vue";
import { CourseService } from "../Course/course.service";
import { restoreAnswerTextItem } from "../Objectives/Quiz/Questions/question.utils";
import { ObjectiveService } from "./objective.service";
const FormInfo = defineAsyncComponent(
  () => import("./InfoGeneral/form-info.vue")
);
const ObjectiveForm = defineAsyncComponent(
  () => import("./Objective/objective.vue")
);

const Quiz = defineAsyncComponent(() => import("../Objectives/Quiz/quiz.vue"));

export default defineComponent({
  name: "formTrail",
  components: { FormInfo, ObjectiveForm, Quiz },
  computed: {
    serviceCourse(): CourseService {
      return new CourseService();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    user(): any {
      return getUserFromStorage();
    },
  },

  data() {
    return {
      home: { label: "", command: () => this.goBack() },
      items: [
        { label: this.$t("labels.add", { pt: "Capítulo", en: "Chapter" }) },
      ],
      course: new Course(),
      chapter: new Chapter(),
      objectives: [] as LearningObjective[],
      quiz: new QuizTO(),
      lessonPlans: [] as LessonPlan[],
      active: 0,
      buttons: [
        {
          title: this.$t("objectives.titles.info"),
          active: 0,
          off: false,
          done: null,
          info: "",
        },
        {
          title: this.$t("objectives.titles.objectives"),
          active: 1,
          off: true,
          done: null,
          info: "",
        },
        {
          title: this.$t("objectives.titles.quiz"),
          active: 2,
          off: true,
          done: null,
          info: "",
        },
      ] as any,
      steps: {
        allowMaterials: false,
        allowQuiz: false,
      } as any,
      objectiveId: "" as any,
      quizId: "" as any,
      params: new QueryParams(),
      loading: true as boolean,
      isEditPage: false as boolean,
      blockScroll: true as boolean,
      predecessorId: "" as string,
      questions: [] as (Question | QuestionTrueFalse | QuestionDragDrop)[],
      showQuiz: false as boolean,
      questionsAbstract: [] as (QuestionTrueFalse | QuestionDragDrop)[],
      questionsDefault: [] as Question[],
    };
  },
  mounted() {
    this.objectiveId = this.$route.params.id;
    this.isEditPage = this.$route.params.id ? true : false;
    this.getCourseById();
    this.getQuestionByQuiz();
    useQuizQuestionsDragNDropStore.setIsAdaptive(false);
    useQuizQuestionsStore.setIsAdaptive(false);
  },
  updated() {
    this.active = Number(this.$route.params.tab) ?? 0;
    this.objectiveId = this.$route.params.id;
    this.allowSteps(this.$route.params.id);
  },
  watch: {
    $route() {
      removeItemLocalStorage("objectiveId");
    },
  },
  provide() {
    return {
      updateQuestionState: computed(() => this.updateDeletedQuestion),
      updateQuizProvide: computed(() => this.updateStatusQuiz),
    };
  },
  methods: {
    goBack(): void {
      const courseId = this.$route.params.courseId;
      this.$router.push({ name: "chapters", params: { id: courseId } });
    },
    getCourseById(): void {
      const courseId: any = this.$route.params.courseId;
      this.serviceCourse.course.subscribe({
        next: (response) => {
          this.course = response;
          setItemLocalStorage("course", JSON.stringify(this.course));
          this.home.label = response.name;
          if (this.course.chapters && this.course.chapters.length > 0) {
            this.predecessorId =
              this.course.chapters[this.course.chapters.length - 1].id;
          }
          this.getChapter();
        },
      });
      this.serviceCourse.getCourseTraditional(courseId);
    },
    getChapter(): void {
      let chapterId = this.$route.params.chapterId;
      if (chapterId) {
        this.course.chapters?.forEach((chapter) => {
          if (chapter.id === chapterId) {
            this.chapter = chapter;
            setItemLocalStorage("objective", JSON.stringify(this.chapter));
            this.objectives = chapter.trails ?? [];
            this.activeMenu();
            this.getQuizByLearningObjects();
          }
        });
      }
    },
    verifyObjective(): boolean {
      let result = false;
      if (this.objectives.length > 0) {
        this.objectives.forEach((element) => {
          if (element.materials && element.materials?.length > 0) {
            result = true;
          }
        });
      } else {
        result = false;
      }
      return result;
    },
    changeTab(index: number): void {
      this.active = index;
      const params = {
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        tab: index,
      };
      this.go.redirectToPage("formChapterTraditional", params);
    },
    allowSteps(active: any): void {
      this.buttons.forEach((e) => {
        if (e.active === active) {
          e.done = true;
          e.off = false;
        }
        if (e.active === active + 1) {
          e.off = false;
        }
      });
    },
    getQuizByLearningObjects(): void {
      if (this.chapter.id) {
        this.service.quiz.pipe(take(2), last()).subscribe({
          next: (response) => {
            if (response) {
              this.quiz = response;
              this.getQuestionByQuiz();
              this.getQuestionDefaultByQuiz();
              this.showQuiz = true;
            } else {
              this.showQuiz = true;
            }
          },
        });
        this.service.getQuizByTeacher(this.objectives[0]?.id);
      } else {
        this.showQuiz = true;
      }
    },
    getQuestionByQuiz(): void {
      this.service.questionsAbstract.pipe(take(2), last()).subscribe({
        next: (response) => {
          restoreAnswerTextItem(response.content);
          this.questionsAbstract = [...response?.content];
          this.questions = [
            ...this.questionsAbstract,
            ...this.questionsDefault,
          ];
          const btn = this.buttons.filter((e) => e.active === 2)[0];
          btn.done =
            Number(this.quiz.numberQuestions) <= this.questions.length
              ? true
              : false;
          btn.off = false;
        },
      });
      if (this.quiz?.id) {
        this.service.getQuestionsByQuiz(this.quiz.id, new QueryParams(0, 100));
        this.service.getQuestionDefaultByQuiz(
          this.quiz.id,
          new QueryParams(0, 100)
        );
      }
    },
    getQuestionDefaultByQuiz(): void {
      this.service.questions.pipe(take(2), last()).subscribe({
        next: (response) => {
          restoreAnswerTextItem(response.content);
          this.questionsDefault = [...response?.content];
          this.questions = [
            ...this.questionsAbstract,
            ...this.questionsDefault,
          ];
          const btn = this.buttons.filter((e) => e.active === 2)[0];
          btn.done =
            Number(this.quiz.numberQuestions) <= this.questions.length
              ? true
              : false;
          btn.off = false;
        },
      });
    },
    activeMenu(): void {
      const btn = this.buttons.filter((e) => e.active === 0)[0];
      btn.done = true;
      const btn1 = this.buttons.filter((e) => e.active === 1)[0];
      btn1.off = false;
      btn1.done = this.verifyObjective();
      const btn2 = this.buttons.filter((e) => e.active === 2)[0];
      btn2.off = !btn1.done;
    },
    updateDeletedQuestion(
      value: (Question | QuestionDragDrop | QuestionTrueFalse)[]
    ): void {
      this.questionsAbstract = this.questionsAbstract.filter((itemAbs) =>
        value.some((itemValue) => itemAbs.id === itemValue.id!)
      );
      this.questionsDefault = this.questionsDefault.filter((itemDef) =>
        value.some((itemValue) => itemDef.id === itemValue.id!)
      );
      this.questions = [...this.questionsAbstract, ...this.questionsDefault];
    },
    updateStatusQuiz(value) {
      this.quiz = value;
      const btn = this.buttons.filter((e) => e.active === 2)[0];
      btn.done =
        Number(this.quiz.numberQuestions) <= this.questions.length
          ? true
          : false;
    },
  },
});
</script>
